import cx from "classnames";
import { inject, observer } from "mobx-react";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationCircle,
  FaEye,
  FaEyeSlash,
  FaRedo,
  FaSignInAlt,
  FaSms,
} from "react-icons/fa";
import { Col, FormGroup, Row } from "reactstrap";
import { Modal } from "reactstrap";
import Button from "../../../../components/Button";
import { MessageResultState } from "../../../../types/general-types";
import { setSessionKey, setUser } from "../../../../utils/SsoSession";
import useCountdown from "../../../../utils/useCountdown";
import MandatoryPasswordRenewalStore from "./store";
import { VerificationTypes } from "./types";

interface IRenewalForm {
  renewalUsername: string;
  oldPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
  otpCode: string;
}

interface IProps {
  MandatoryPasswordRenewalStore?: MandatoryPasswordRenewalStore;
  username: string;
  password?: string;
  isShow: boolean;
  onClose?: () => void;
  clientId?: string | null;
}

const MandatoryPasswordRenewalModal: FC<IProps> = inject(
  "MandatoryPasswordRenewalStore"
)(
  observer((props: IProps) => {
    const { MandatoryPasswordRenewalStore: store, username, password, isShow, onClose, clientId } = props;
    const { t } = useTranslation();

    const [step, setStep] = useState<
      "password" | "verificationType" | "otp" | "success"
    >("password");
    const [verificationType, setVerificationType] = useState<
      VerificationTypes | undefined
    >(undefined);
    const [timer, setTimer] = useCountdown(0);
    const [visible, setVisible] = useState({
      oldPassword: false,
      newPassword: false,
      newPasswordRepeat: false,
    });

    const toggleVisible = useCallback(
      (field: "oldPassword" | "newPassword" | "newPasswordRepeat") =>
        setVisible((prev) => ({ ...prev, [field]: !prev[field] })),
      []
    );

    const [checkingOldPassword, setCheckingOldPassword] =
      useState<boolean>(false);

    const credentialsRef = useRef<{
      username: string;
      oldPassword: string;
      newPassword: string;
      newPasswordRepeat: string;
    }>({ username: "", oldPassword: "", newPassword: "", newPasswordRepeat: "" });

    const {
      control,
      errors,
      formState,
      watch,
      getValues,
      setValue,
      setError,
      clearErrors,
      handleSubmit,
    } = useForm<IRenewalForm>({
      defaultValues: {
        renewalUsername: username ?? "",
        oldPassword: password ?? "",
        newPassword: "",
        newPasswordRepeat: "",
        otpCode: "",
      },
    });

    const enteredUsername = watch("renewalUsername");
    const oldPassword = watch("oldPassword");
    const newPassword = watch("newPassword");
    const newPasswordRepeat = watch("newPasswordRepeat");

    const rules = useMemo(
      () => [
        {
          label: "Password must be at least 14 characters long.",
          test: (pw: string) => pw.length >= 14,
        },
        {
          label: "Password must contain at least 1 number.",
          test: (pw: string) => /[0-9]/.test(pw),
        },
        {
          label: "Password must contain at least 1 special character.",
          test: (pw: string) => /[.,!?@#$%^&*()\-_+=]/.test(pw),
        },
        {
          label: "Password must contain at least 1 uppercase letter.",
          test: (pw: string) => /[A-Z]/.test(pw),
        },
        {
          label: "Password must contain at least 1 lowercase letter.",
          test: (pw: string) => /[a-z]/.test(pw),
        },
      ],
      []
    );

    const allRulesPass = useMemo(
      () => rules.every((rule) => rule.test(newPassword || "")),
      [rules, newPassword]
    );
    const passwordsMatch =
      !!newPassword && newPassword === newPasswordRepeat;
    const canSubmitStep1 =
      !!enteredUsername && !!oldPassword && allRulesPass && passwordsMatch;

    const sendOtp = useCallback(async () => {
      if (!store || !verificationType) return;
      await store
        .SendOtp({ username: credentialsRef.current.username, verificationType })
        .then((res) => {
          if (res.state === MessageResultState.SUCCESS) {
            setValue("otpCode", "");
            setTimer(res.result?.verificationSeconds ?? 0);
            setStep("otp");
          }
        });
    }, [store, verificationType, setValue, setTimer]);

    const changePassword = useCallback(async () => {
      if (!store || !getValues("otpCode")) return;
      await store
        .ChangePassword({
          username: credentialsRef.current.username,
          oldPassword: credentialsRef.current.oldPassword,
          newPassword: credentialsRef.current.newPassword,
          newPasswordRepeat: credentialsRef.current.newPasswordRepeat,
          otpCode: getValues("otpCode"),
          clientId: clientId ?? null,
        })
        .then((res) => {
          if (res.state === MessageResultState.SUCCESS) {
            setStep("success");
            const session = res.result;
            setTimeout(() => {
              if (session?.sessionKey) {
                setSessionKey(session.sessionKey);
                setUser(session.user);
                const returnUrl = new URLSearchParams(
                  window.location.search
                ).get("returnUrl");
                window.location.href = session.redirectUrl
                  ? session.redirectUrl +
                    "?sessionKey=" +
                    session.sessionKey +
                    (returnUrl && returnUrl !== "/"
                      ? "&returnUrl=" + returnUrl
                      : "")
                  : "portal-selection";
              } else {
                window.location.href = window.location.origin + "/login";
              }
            }, 3456);
          } else {
            onClose?.();
          }
        })
        .catch(() => {
          onClose?.();
        });
    }, [store, getValues, onClose, clientId]);

    const proceedToVerification = useCallback(async () => {
      if (!store || !canSubmitStep1 || checkingOldPassword) return;
      const renewalUsername = getValues("renewalUsername");
      const oldPassword = getValues("oldPassword");
      const newPassword = getValues("newPassword");
      setCheckingOldPassword(true);
      try {
        const res = await store.Validate({
          username: renewalUsername,
          oldPassword,
          newPassword,
        });
        if (res.state !== MessageResultState.SUCCESS) {
          setError("oldPassword", {
            type: "manual",
            message: "The old password you entered is incorrect.",
          });
          return;
        }

        let hasError = false;
        if (res.result?.oldPasswordValid === false) {
          setError("oldPassword", {
            type: "manual",
            message: "The old password you entered is incorrect.",
          });
          hasError = true;
        } else {
          clearErrors("oldPassword");
        }
        if (res.result?.newPasswordSameAsOld === true) {
          setError("newPassword", {
            type: "manual",
            message:
              "Your new password cannot be the same as your current password.",
          });
          hasError = true;
        } else if (res.result?.newPasswordInLastTwo === true) {
          setError("newPassword", {
            type: "manual",
            message: "Your password must be different from your last 2 passwords.",
          });
          hasError = true;
        } else {
          clearErrors("newPassword");
        }
        if (hasError) return;

        credentialsRef.current = {
          username: renewalUsername,
          oldPassword,
          newPassword,
          newPasswordRepeat: getValues("newPasswordRepeat"),
        };
        setStep("verificationType");
      } catch {
        setError("oldPassword", {
          type: "manual",
          message: "The old password you entered is incorrect.",
        });
      } finally {
        setCheckingOldPassword(false);
      }
    }, [
      store,
      canSubmitStep1,
      checkingOldPassword,
      getValues,
      setError,
      clearErrors,
    ]);

    const PasswordStep = useMemo(() => {
      return (
        <div className="card-body">
          <div className="card-title mb-2 fw-semibold">
            <h5 className="text-center w-100 m-0">
              {t("Your password has expired")}
            </h5>
          </div>
          <p className="text-center text-muted small mb-4">
            {t("Your password must be renewed every 6 months.")}
          </p>
          <Row>
            <Col md={12}>
              <FormGroup>
                <label className="form-label">{t("Username")}</label>
                <Controller
                  name={"renewalUsername"}
                  control={control}
                  render={(field) => (
                    <input
                      {...field}
                      id={"renewalUsername"}
                      type="text"
                      autoComplete="off"
                      disabled={true}
                      className="form-control"
                      placeholder={t("Username")}
                    />
                  )}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <label className="form-label">{t("Old Password")}</label>
                <Controller
                  name={"oldPassword"}
                  control={control}
                  render={(field) => (
                    <div className="position-relative">
                      <input
                        {...field}
                        id={"oldPassword"}
                        type={visible.oldPassword ? "text" : "password"}
                        autoComplete="off"
                        disabled={true}
                        className={cx("form-control", {
                          "is-invalid": errors.oldPassword,
                        })}
                        style={{ paddingRight: "40px" }}
                        placeholder={t("Old Password")}
                      />
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={() => toggleVisible("oldPassword")}
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: errors.oldPassword ? "#dc3545" : "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        {visible.oldPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  )}
                />
                {errors.oldPassword && (
                  <div className="invalid-feedback d-block d-flex align-items-center mt-1">
                    <FaExclamationCircle className="me-1 flex-shrink-0" />
                    <span>{t(errors.oldPassword.message ?? "")}</span>
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <label className="form-label">{t("New Password")}</label>
                <Controller
                  name={"newPassword"}
                  control={control}
                  rules={{
                    required: t("*Please fill out this field."),
                    maxLength: {
                      value: 100,
                      message: t(
                        "The given value should be smaller than {{ value }}.",
                        { value: 100 }
                      ),
                    },
                  }}
                  render={(field) => (
                    <div className="position-relative">
                      <input
                        {...field}
                        id={"newPassword"}
                        type={visible.newPassword ? "text" : "password"}
                        autoComplete="new-password"
                        disabled={formState.isSubmitting || checkingOldPassword}
                        className={cx("form-control", {
                          "is-invalid": errors.newPassword,
                        })}
                        style={{ paddingRight: "40px" }}
                        placeholder={t("New Password")}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          if (errors.newPassword) clearErrors("newPassword");
                        }}
                      />
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={() => toggleVisible("newPassword")}
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: errors.newPassword ? "#dc3545" : "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        {visible.newPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  )}
                />
                {errors.newPassword && (
                  <div className="invalid-feedback d-block d-flex align-items-center mt-1">
                    <FaExclamationCircle className="me-1 flex-shrink-0" />
                    <span>{t(errors.newPassword.message ?? "")}</span>
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <label className="form-label">
                  {t("New Password (Repeat)")}
                </label>
                <Controller
                  name={"newPasswordRepeat"}
                  control={control}
                  rules={{
                    required: t("*Please fill out this field."),
                    maxLength: {
                      value: 100,
                      message: t(
                        "The given value should be smaller than {{ value }}.",
                        { value: 100 }
                      ),
                    },
                  }}
                  render={(field) => (
                    <div className="position-relative">
                      <input
                        {...field}
                        id={"newPasswordRepeat"}
                        type={visible.newPasswordRepeat ? "text" : "password"}
                        autoComplete="new-password"
                        disabled={formState.isSubmitting}
                        className="form-control"
                        style={{ paddingRight: "40px" }}
                        placeholder={t("New Password (Repeat)")}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={() => toggleVisible("newPasswordRepeat")}
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "12px",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#6c757d",
                        }}
                      >
                        {visible.newPasswordRepeat ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  )}
                />
                {!!newPasswordRepeat && !passwordsMatch && (
                  <div className="invalid-feedback d-block">
                    {t("The new passwords do not match.")}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md={12} className="mt-2">
              <ul className="mt-2">
                {rules.map((rule, index) => {
                  const isValid = rule.test(newPassword || "");
                  return (
                    <li
                      key={index}
                      className={cx("", {
                        "text-success": isValid,
                        "text-danger": !isValid,
                      })}
                    >
                      {t(rule.label)}
                    </li>
                  );
                })}
                <li key={"history-info"} className="text-muted">
                  {t("Your password must be different from your last 2 passwords.")}
                </li>
              </ul>
            </Col>
            <div className="divider" />
            <Col md={12}>
              <div className="text-center pt-2">
                <Button
                  className="btn btn-primary align-items-center"
                  onClick={handleSubmit(async () => {
                    await proceedToVerification();
                  })}
                  disabled={
                    !canSubmitStep1 ||
                    formState.isSubmitting ||
                    checkingOldPassword
                  }
                  icon={
                    checkingOldPassword ? (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <FaArrowRight className="me-2" />
                    )
                  }
                  text={checkingOldPassword ? t("Checking...") : t("Continue")}
                />
              </div>
            </Col>
          </Row>
        </div>
      );
    }, [
      canSubmitStep1,
      checkingOldPassword,
      clearErrors,
      control,
      errors.oldPassword,
      errors.newPassword,
      formState.isSubmitting,
      handleSubmit,
      newPassword,
      newPasswordRepeat,
      passwordsMatch,
      proceedToVerification,
      rules,
      t,
      visible,
      toggleVisible,
    ]);

    const VerificationTypeStep = useMemo(() => {
      return (
        <div className="card-body">
          <div className="card-title mb-4 fw-semibold">
            <h5 className="text-center w-100 m-0">
              {t("Verification Type")} <span className="text-danger">*</span>
            </h5>
          </div>

          <div className="form-check mb-3">
            <input
              type="radio"
              name="renewalVerificationTypeSms"
              id="renewalVerificationTypeSms"
              className="form-check-input"
              checked={verificationType === VerificationTypes.Sms}
              onChange={() => setVerificationType(VerificationTypes.Sms)}
              style={{ transform: "scale(1.2)" }}
              disabled={formState.isSubmitting}
            />
            <label
              className="form-check-label align-items-center ms-2"
              htmlFor="renewalVerificationTypeSms"
              style={{
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                color:
                  verificationType === VerificationTypes.Sms
                    ? "#007bff"
                    : "#555",
              }}
            >
              <FaSms className="me-2" style={{ fontSize: "18px" }} />
              {t("Sms")}
            </label>
          </div>

          <div className="form-check mb-4">
            <input
              type="radio"
              name="renewalVerificationTypeEmail"
              id="renewalVerificationTypeEmail"
              className="form-check-input"
              checked={verificationType === VerificationTypes.Email}
              onChange={() => setVerificationType(VerificationTypes.Email)}
              style={{ transform: "scale(1.2)" }}
              disabled={formState.isSubmitting}
            />
            <label
              className="form-check-label align-items-center ms-2"
              htmlFor="renewalVerificationTypeEmail"
              style={{
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                color:
                  verificationType === VerificationTypes.Email
                    ? "#007bff"
                    : "#555",
              }}
            >
              <FaEnvelope className="me-2" style={{ fontSize: "18px" }} />
              {t("Email")}
            </label>
          </div>

          <div className="text-center pt-2">
            <Button
              className="btn btn-primary align-items-center me-4"
              onClick={() => setStep("password")}
              icon={<FaArrowLeft className="me-2" />}
              text={t("Back")}
            />
            <Button
              className="btn btn-primary align-items-center ms-4"
              onClick={handleSubmit(async () => {
                await sendOtp();
              })}
              disabled={!verificationType || formState.isSubmitting}
              icon={<FaArrowRight className="me-2" />}
              text={t("Continue")}
            />
          </div>
        </div>
      );
    }, [handleSubmit, sendOtp, t, formState.isSubmitting, verificationType]);

    const OtpStep = useMemo(() => {
      return (
        <div className="card-body">
          <div className="card-title mb-4 fw-semibold">
            <h5 className="text-center w-100 m-0">
              {t("Verification Code")} <span className="text-danger">*</span>
            </h5>
          </div>

          <div className="mb-4 d-flex justify-content-center">
            <Controller
              name="otpCode"
              control={control}
              rules={{
                required: t("Please fill out this field."),
                minLength: {
                  value: 4,
                  message: t("The given value must be {{value}} characters..", {
                    value: 4,
                  }),
                },
                maxLength: {
                  value: 8,
                  message: t("The given value must be {{value}} characters.", {
                    value: 8,
                  }),
                },
              }}
              render={(field) => (
                <input
                  {...field}
                  minLength={4}
                  maxLength={8}
                  type="text"
                  placeholder={t("Verification Code")}
                  style={{ width: "50%" }}
                  disabled={formState.isSubmitting || timer === 0}
                  className={cx("form-control text-center ", {
                    "is-invalid": errors["otpCode"],
                  })}
                  onKeyDown={(
                    event: React.KeyboardEvent<HTMLInputElement>
                  ) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleSubmit(async () => {
                        await changePassword();
                      })();
                    }
                  }}
                />
              )}
            />
          </div>

          <div className="text-center mb-3">
            {timer && timer > 0 ? (
              <span>
                {t("Remaining Time:")} {timer} {t("seconds")}
              </span>
            ) : (
              <Button
                className="btn btn-warning align-items-center"
                onClick={sendOtp}
                icon={<FaRedo />}
                tooltipText={t("Resend Code")}
              />
            )}
          </div>

          <div className="text-center pt-4">
            <Button
              className="btn btn-primary align-items-center me-4"
              onClick={() => setStep("verificationType")}
              icon={<FaArrowLeft className="me-2" />}
              text={t("Back")}
              disabled={timer && timer > 0 ? true : false}
            />
            <Button
              className="btn btn-success align-items-center ms-4"
              onClick={handleSubmit(async () => {
                await changePassword();
              })}
              icon={<FaSignInAlt className="me-2" />}
              text={t("Verify")}
              disabled={
                timer === undefined || timer === 0 || formState.isSubmitting
              }
            />
          </div>
        </div>
      );
    }, [
      changePassword,
      control,
      errors,
      formState.isSubmitting,
      handleSubmit,
      sendOtp,
      t,
      timer,
    ]);

    const SuccessStep = useMemo(() => {
      return (
        <div className="card-body text-center">
          <FaCheckCircle
            className="text-success mb-3"
            style={{ fontSize: "48px" }}
          />
          <h5 className="mb-3">
            {t("Your password has been changed successfully.")}
          </h5>
          <p className="text-muted">
            {t("You will be redirected to the login page in three seconds..")}
          </p>
        </div>
      );
    }, [t]);

    return (
      <Modal
        isOpen={isShow}
        backdrop="static"
        keyboard={false}
        className="verify-modal"
      >
        <div
          className="card w-100 border-0"
          style={{ borderRadius: "15px" }}
        >
          {step === "password" && PasswordStep}
          {step === "verificationType" && VerificationTypeStep}
          {step === "otp" && OtpStep}
          {step === "success" && SuccessStep}
        </div>
      </Modal>
    );
  })
);

export default MandatoryPasswordRenewalModal;
